// src/components/TrialCardArchive.jsx
import React from 'react';
import { Clock, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import VerdictMemeFrame, { deriveOverlayVerdictLine } from './VerdictMemeFrame';
import FollowButton from './FollowButton';

function isBuiltVerdictCardAsset(url) {
  const normalized = String(url || '').toLowerCase();
  return normalized.includes('/verdict-card.svg') || normalized.includes('/premium-verdict-card.svg');
}

export default function TrialCardArchive({
  trial,
  onRedoTrial,
  onBeforeOpen, // <-- add this prop
  linkTo,
  currentUserId = null,
}) {
  const {
    id, title, side_a, side_b,
    image_url, image_emoji,
    votes_a, votes_b,
    total_votes, closed_at,
    punchline,
    verdict_ready,
    verdict_failed,
    renderable_text,
    creator_id_canonical,
    creator_username,
    creator_call_label,
    creator_call_outcome,
  } = trial;

  const total = Number(total_votes || 0);
  const a = Number(votes_a || 0);
  const b = Number(votes_b || 0);
  const pctA = total ? Math.round((a / total) * 100) : 0;
  const pctB = total ? 100 - pctA : 0;
  const winner = a === b ? 'Tie' : (a > b ? side_a : side_b);
  const loser = a === b ? null : (a > b ? side_b : side_a);

  const closed = closed_at ? new Date(closed_at) : null;
  const closedFmt = closed ? closed.toLocaleString() : '—';

  const verdictLineFull = trial?.subverdict_line || trial?.verdict_line_full || trial?.verdict_line || renderable_text || punchline || null;
  const verdictLineShort = trial?.overlay_line || trial?.verdict_line_short || deriveOverlayVerdictLine(verdictLineFull);
  const verdictLine = verdictLineShort || verdictLineFull || null;
  const cardVerdictLine = verdictLineFull || verdictLine || null;
  const renderState = verdict_failed ? 'failed' : (verdict_ready ? 'ready' : 'processing');
  const hasEmbeddedVerdictText = isBuiltVerdictCardAsset(image_url);

  const margin = Math.abs(pctA - 50);
  const isClose = total > 0 && margin <= 5;
  const isLandslide = total > 0 && margin >= 25;
  const isYourCall = Boolean(
    currentUserId &&
    creator_id_canonical &&
    String(currentUserId) === String(creator_id_canonical)
  );

  const targetUrl = linkTo || `/t/${id}`;
  return (
    <Link
      to={targetUrl}
      onClick={onBeforeOpen ? onBeforeOpen : undefined} // <-- safe usage
      className="block rounded-xl overflow-hidden bg-slate-800 border border-slate-700 hover:border-slate-600 transition"
    >
      <VerdictMemeFrame
        id={id}
        title={title}
        sideA={side_a}
        sideB={side_b}
        verdictLine={verdictLine}
        verdictLineShort={verdictLineShort}
        verdictLineFull={verdictLineFull}
        imageUrl={image_url}
        assetSource={trial}
        emoji={image_emoji}
        winnerLabel={winner === 'Tie' ? 'Tie' : winner}
        loserLabel={loser || `${side_a} vs ${side_b}`}
        isClose={isClose}
        isLandslide={isLandslide}
        renderState={renderState}
        showEmoji={false}
        surface="vault-trial"
        heightClass={hasEmbeddedVerdictText ? 'h-44' : 'h-52'}
      />

      <div className="p-3">
        <div className="text-slate-100 font-semibold leading-tight line-clamp-2">{title}</div>
        <div className="text-slate-400 text-sm mt-1 line-clamp-1">
          {side_a} vs {side_b}
        </div>
        {isYourCall && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center rounded-full border border-emerald-400/25 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-200">
              Your call
            </div>
            {creator_call_label && (
              <div className="inline-flex items-center rounded-full border border-purple-400/20 bg-purple-500/10 px-2.5 py-1 text-[11px] font-semibold text-purple-100">
                You backed {creator_call_label}
              </div>
            )}
          </div>
        )}

        <div className="mt-3 rounded-lg border border-slate-700/70 bg-slate-900/45 px-3 py-2">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 text-sm text-slate-200">
              {cardVerdictLine && verdict_ready ? (
                <div className="line-clamp-2 text-sm leading-tight text-slate-200">
                  {cardVerdictLine}
                </div>
              ) : a === b ? (
                <span className="font-semibold">Tie</span>
              ) : (
                <>
                  <span className="font-semibold text-white">{winner}</span>
                  <span className="text-slate-400"> took it</span>
                </>
              )}
            </div>
            <div className="shrink-0 text-xs text-slate-400">{total} votes</div>
          </div>

          {total > 0 && (
            <div className="mt-3 space-y-2">
              <div className="flex items-center justify-between text-[11px] text-slate-400">
                <span className="truncate pr-2">{side_a}</span>
                <span>{pctA}%</span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-700">
                <div className="h-1.5 bg-purple-500" style={{ width: `${pctA}%` }} />
              </div>

              <div className="flex items-center justify-between text-[11px] text-slate-400">
                <span className="truncate pr-2">{side_b}</span>
                <span>{pctB}%</span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-700">
                <div className="h-1.5 bg-amber-400" style={{ width: `${pctB}%` }} />
              </div>
            </div>
          )}
        </div>

        {typeof onRedoTrial === 'function' && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRedoTrial();
            }}
            className="mt-3 w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold py-2 rounded-lg flex items-center justify-center gap-2 hover:scale-105 transition"
            aria-label="Redo this trial"
          >
            <Zap className="w-4 h-4" />
            Redo This Trial
          </button>
        )}

        {/* Creator row with follow button */}
        {creator_id_canonical && (
          <div
            className="mt-3 flex items-center justify-between py-2 px-3 bg-slate-900/50 rounded-lg border border-slate-700/50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                {(creator_username || '?')[0].toUpperCase()}
              </div>
              <div className="leading-tight">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">
                  {isYourCall ? 'Your receipt' : 'Created by'}
                </div>
                {creator_username ? (
                  <Link
                    to={`/user/${creator_username}`}
                    className="text-xs text-slate-300 font-medium hover:text-purple-300 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    @{creator_username}
                  </Link>
                ) : (
                  <div className="text-xs text-slate-300 font-medium">@anonymous</div>
                )}
              </div>
            </div>
            <FollowButton
              targetUserId={creator_id_canonical}
              targetUsername={creator_username || 'anonymous'}
            />
          </div>
        )}

        <div className="flex items-center justify-between mt-3 text-xs text-slate-400">
          <div className="font-medium text-slate-500">
            {isYourCall
              ? (
                creator_call_outcome === 'won'
                  ? 'Call landed'
                  : creator_call_outcome === 'lost'
                    ? 'Call missed'
                    : creator_call_outcome === 'push'
                      ? 'Call pushed'
                      : 'Proof landed'
              )
              : 'Receipt ready'}
          </div>
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            <span>{closedFmt}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
